import { BPS_DENOMINATOR, GIFT_RARITY_STAR_EQUIVALENT, PLATFORM_FEE_BPS, VFX_TIER_THRESHOLDS, } from './constants.js';
export function splitStars(gross, feeBps = PLATFORM_FEE_BPS) {
    if (!Number.isInteger(gross) || gross < 0) {
        throw new RangeError(`gross must be a non-negative integer, received ${gross}`);
    }
    if (!Number.isInteger(feeBps) || feeBps < 0 || feeBps > BPS_DENOMINATOR) {
        throw new RangeError(`feeBps must be within [0, ${BPS_DENOMINATOR}], received ${feeBps}`);
    }
    // Floor the fee so the receiver never loses a Star to rounding.
    const fee = Math.floor((gross * feeBps) / BPS_DENOMINATOR);
    return { gross, fee, net: gross - fee, feeBps };
}
/** Star-equivalent valuation of a gift, used purely for VFX tiering. */
export function valueOfGift(rarity) {
    return GIFT_RARITY_STAR_EQUIVALENT[rarity];
}
/**
 * Resolve the VFX tier for a donation.
 *
 * A legendary gift is always a whale event: its floor price is volatile but its
 * social signal is not.
 */
export function resolveTier(input) {
    const giftValue = input.giftRarity ? valueOfGift(input.giftRarity) : 0;
    const valuationStars = Math.max(input.amountStars, giftValue, 1);
    if (input.giftRarity === 'LEGENDARY') {
        return { tier: 'WHALE', valuationStars };
    }
    if (valuationStars >= VFX_TIER_THRESHOLDS.whale.min) {
        return { tier: 'WHALE', valuationStars };
    }
    if (valuationStars >= VFX_TIER_THRESHOLDS.major.min) {
        return { tier: 'MAJOR', valuationStars };
    }
    return { tier: 'MICRO', valuationStars };
}
/** Nanoton <-> TON helpers. Nanotons are handled as bigint to stay exact. */
export const NANOTON_PER_TON = 1000000000n;
export function tonToNano(ton) {
    const asString = typeof ton === 'number' ? ton.toString() : ton.trim();
    if (!/^\d+(\.\d{1,9})?$/.test(asString)) {
        throw new RangeError(`Invalid TON amount: ${asString}`);
    }
    const [whole = '0', fraction = ''] = asString.split('.');
    const padded = fraction.padEnd(9, '0');
    return BigInt(whole) * NANOTON_PER_TON + BigInt(padded || '0');
}
export function nanoToTon(nano, decimals = 2) {
    const value = typeof nano === 'bigint' ? nano : BigInt(nano);
    const whole = value / NANOTON_PER_TON;
    const fraction = (value % NANOTON_PER_TON).toString().padStart(9, '0');
    const trimmed = fraction.slice(0, decimals);
    return decimals > 0 ? `${whole}.${trimmed}` : whole.toString();
}
/** Compact display formatting: 1240 -> "1.2K", 2_400_000 -> "2.4M". */
export function formatCompact(value) {
    if (!Number.isFinite(value))
        return '0';
    const abs = Math.abs(value);
    if (abs < 1_000)
        return value.toString();
    if (abs < 1_000_000)
        return `${trimZero(value / 1_000)}K`;
    if (abs < 1_000_000_000)
        return `${trimZero(value / 1_000_000)}M`;
    return `${trimZero(value / 1_000_000_000)}B`;
}
function trimZero(value) {
    const fixed = value.toFixed(1);
    return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed;
}
/** Leaderboard bucket keys. Daily/weekly buckets are computed in UTC. */
export function dailyBucket(now = new Date()) {
    return now.toISOString().slice(0, 10);
}
export function weeklyBucket(now = new Date()) {
    // ISO-8601 week number, Monday-based.
    const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const dayNumber = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - dayNumber);
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const week = Math.ceil(((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
    return `${date.getUTCFullYear()}-W${week.toString().padStart(2, '0')}`;
}
